import { stringify } from "querystring";
import { ChipGroup, ErrorFilledSVG, Icon } from "@newsela/angelou";
import { ReactNode, useEffect, useRef, useState } from "react";

import { useRouter } from "next/router";
import { screenReaderOnlyCss } from "@/components/components.style";
import { AIChip } from "@/components/AIComponents/AIChip";
import AiLunaTagFullIcon from "@/icons/AILunaTagFull/AILunaTagFullIcon";
import AIBetaTag from "@/components/AIComponents/AIBetaTag/AIBetaTag";
import AIButton from "@/components/AIComponents/AiButton";
import AIToggle from "@/components/AIComponents/AIToggle";
import AILoadingSpinner from "@/components/AIComponents/AILoadingSpinner";
import LunaPeekingSideIcon from "@/icons/LunaPeekingSideIcon/LunaPeekingSideIcon";
import { useFilterControls } from "@/search/Langston/useFilterControls";
import LunaConfusedIcon from "@/icons/LunaConfusedIcon/LunaConfusedIcon";
import LunaThinkingIcon from "@/icons/LunaThinkingIcon/LunaThinkingIcon";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { useSearchWithLunaContext } from "@/search/Langston/SearchWithLunaProvider";
import VisibilityPermissions from "../VisibilityPermissions/VisibilityPermissions";
import { LUNA_FILTER_PANEL_TEST_IDS } from "./LunaFilterPanel.constants";
import { LunaFilterPanelProps } from "./LunaFilterPanel.types";
import {
  panelRootCss,
  panelCardCss,
  badgesRowCss,
  sectionTitleCss,
  sectionDescriptionCss,
  subsectionTitleCss,
  chipsGroupCss,
  chipItemWrapperCss,
  primaryActionButtonCss,
  toggleWrapperCss,
  promptSectionCss,
  illustrationAffixTopRightCss,
  illustrationAffixBottomRightCss,
  textAreaOverridesCss,
  emptyStateContainerCss,
  loadingBoxCss,
  refinementLoadingBoxCss,
  visibilityPermissionCss,
  lunaFullTagCss,
  labelPromptCss,
  textareaContainerCss,
  errorLabelCss,
  errorIconCss,
  tipDescriptionCss,
  lunaTipIconBottomRightCss,
  lunaTipDescriptionWrapperCss,
} from "./LunaFilterPanel.styles";
import { formatSelectedFilters, LunaChip } from "./LunaFilterPanel.utils";

export const LunaFilterPanel = ({
  className,
  testId,
  isAISearchEnabled,
  onToggle,
}: LunaFilterPanelProps) => {
  const { push, query, pathname } = useRouter();
  const {
    isInvalidLunaPrompt,
    isLunaError,
    isTrafficCopFetching,
    selectedLunaChips,
    setSelectedLunaChips,
    setTrafficCopPrompt,
    trafficCopPrompt,
    trafficCopResponse,
    initialPrompt,
  } = useSearchWithLunaContext();

  const { handleRemoveFilter } = useFilterControls();

  const filteredValues = formatSelectedFilters(selectedLunaChips || []);
  const [refinementError, setRefinementError] = useState(false);
  const refinementTextareaRef = useRef<HTMLTextAreaElement>(null);

  const hasPrompt = Boolean(
    trafficCopPrompt && trafficCopPrompt.trim().length > 0,
  );

  const handleChipEvent = (_: unknown, value: string) => {
    handleRemoveFilter("", value);
    setSelectedLunaChips(
      selectedLunaChips.filter((chip: LunaChip) => {
        const chipValueFormatted = `${chip.type},${chip.value}`;

        return value !== chipValueFormatted;
      }),
    );
  };

  const onSubmitRefinement = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!refinementTextareaRef.current?.value.trim()) {
      setRefinementError(true);
      return;
    }
    setRefinementError(false);
    setTrafficCopPrompt(refinementTextareaRef.current?.value || "");
  };

  const onRefinementKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (refinementError) {
      setRefinementError(false);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      onSubmitRefinement();
    }
  };

  const handleSearchWithLunaToggle = () => {
    const newValue = !isAISearchEnabled;
    if (pathname === "/search") {
      const searchParams = new URLSearchParams(stringify(query));
      searchParams.delete(LangstonParameterSchema.prompt.key);
      void push({
        pathname: "/search",
        search: `?${searchParams.toString()}`,
      });
    }
    onToggle(newValue);
  };

  const isPostRefinement =
    !isTrafficCopFetching && !trafficCopResponse?.response;
  const refinementQuestion =
    !isTrafficCopFetching && trafficCopResponse?.response;

  useEffect(() => {
    if (refinementQuestion) {
      refinementTextareaRef.current?.focus();
    }
  }, [refinementQuestion]);

  useEffect(() => {
    if (refinementError && refinementTextareaRef.current) {
      refinementTextareaRef.current.focus();
    }
  }, [refinementError]);

  const renderBadges = (): ReactNode => (
    <div css={badgesRowCss}>
      <div css={lunaFullTagCss}>
        <AiLunaTagFullIcon />
      </div>
      <AIBetaTag />
    </div>
  );

  const renderErrorState = (): ReactNode => (
    <div css={emptyStateContainerCss} aria-live="polite">
      <VisibilityPermissions containerCssFn={visibilityPermissionCss} />
      <h2 css={sectionTitleCss}>Something went wrong.</h2>
      <p css={sectionDescriptionCss}>
        Luna couldn't complete your request right now. Please try again in a few
        moments.
      </p>
      <div css={illustrationAffixBottomRightCss}>
        <LunaConfusedIcon aria-hidden />
      </div>
    </div>
  );

  const renderResults = (): ReactNode => (
    <div>
      <VisibilityPermissions containerCssFn={visibilityPermissionCss} />
      <h2 css={sectionTitleCss}>Most relevant results for:</h2>
      <p css={sectionDescriptionCss}>"{initialPrompt}"</p>

      {filteredValues.length ? (
        <h2 css={subsectionTitleCss}>Filters applied by Luna:</h2>
      ) : null}

      <ChipGroup
        groupName={LUNA_FILTER_PANEL_TEST_IDS.CHIP_GROUP}
        chipType={ChipGroup.chipTypes.input}
        onChipEvent={handleChipEvent}
        size={ChipGroup.chipSizes.medium}
        __cssFor={chipsGroupCss}
      >
        {filteredValues.map((item: LunaChip) => (
          <div key={item.value} css={chipItemWrapperCss} title={item.label}>
            <AIChip
              active
              text={item.label}
              value={`${item.type},${item.value}`}
              dataProps={{
                "data-qa-selector": `${LUNA_FILTER_PANEL_TEST_IDS.CHIP_PREFIX}${item.type}-${item.value}`,
                "data-chip-index": String(item.value),
              }}
            />
          </div>
        ))}
      </ChipGroup>

      {isPostRefinement ? null : renderPromptRefinement()}
    </div>
  );

  const renderPromptRefinement = (): ReactNode => (
    <form onSubmit={onSubmitRefinement} css={promptSectionCss}>
      {isTrafficCopFetching ? (
        <div
          css={refinementLoadingBoxCss}
          role="status"
          aria-live="polite"
          aria-busy
          aria-label="Loading refinement"
          data-testid="luna-refinement-loading"
        >
          <AILoadingSpinner
            dataProps={{ "data-testid": "luna-refinement-spinner" }}
          />
        </div>
      ) : (
        <>
          <div css={illustrationAffixTopRightCss}>
            <LunaPeekingSideIcon aria-hidden />
          </div>

          <h2 css={subsectionTitleCss}>Question from Luna:</h2>
          <label htmlFor="luna-refinement-textarea" css={labelPromptCss}>
            {refinementQuestion}
          </label>
          <div css={textareaContainerCss}>
            <textarea
              ref={refinementTextareaRef}
              css={textAreaOverridesCss}
              id="luna-refinement-textarea"
              placeholder="e.g., “Culture” or “History”"
              onKeyDown={onRefinementKeyDown}
              rows={3}
              aria-invalid={!!refinementError}
              aria-describedby={
                refinementError ? "luna-refinement-error" : undefined
              }
            />
            {refinementError && (
              <div role="alert" css={errorLabelCss} id="luna-refinement-error">
                <Icon
                  __cssFor={{ root: errorIconCss }}
                  SvgComponent={ErrorFilledSVG}
                  isDecorative={false}
                  title="Error"
                />
                Please provide a refinement.
              </div>
            )}
          </div>
        </>
      )}
      {!isTrafficCopFetching && (
        <AIButton __cssFor={{ root: primaryActionButtonCss }} type="submit">
          Refine search
        </AIButton>
      )}
    </form>
  );

  const renderEmptyState = (): ReactNode => (
    <div css={emptyStateContainerCss} aria-live="polite">
      <VisibilityPermissions containerCssFn={visibilityPermissionCss} />
      <h2 css={sectionTitleCss}>Start a new search with Luna.</h2>
      <p css={sectionDescriptionCss}>
        Tell Luna what you need to find. You can improve your search by adding
        topics, grade levels, or types of content to your prompt.
      </p>
      <div css={illustrationAffixBottomRightCss}>
        <LunaThinkingIcon aria-hidden />
      </div>
    </div>
  );

  // Instead of a "prompt needs revision" state, we now take the invalid
  // query and pass it into Langston as a regular search query.
  // We'll display the query with no other Luna functionality applied.
  const renderPromptNeedsRevision = (): ReactNode => (
    <div css={emptyStateContainerCss} aria-live="polite">
      <VisibilityPermissions containerCssFn={visibilityPermissionCss} />
      <h2 css={sectionTitleCss}>Most relevant results for:</h2>
      <p css={sectionDescriptionCss}>"{initialPrompt}"</p>

      <h2 css={subsectionTitleCss}>Luna tip:</h2>
      <div css={lunaTipDescriptionWrapperCss}>
        <div>
          <p css={{ ...sectionDescriptionCss, ...tipDescriptionCss }}>
            You can achieve more precise results by using detailed prompts, such
            as:
          </p>
          <p css={sectionTitleCss}>
            "3rd grade articles about the concept of community helpers"
          </p>
        </div>
        <div css={lunaTipIconBottomRightCss}>
          <LunaPeekingSideIcon idea aria-hidden />
        </div>
      </div>
    </div>
  );

  const renderContent = (): ReactNode => {
    if (isLunaError) return renderErrorState();
    if (isInvalidLunaPrompt) return renderPromptNeedsRevision();
    if (!hasPrompt) return renderEmptyState();
    return renderResults();
  };

  return (
    <div
      css={panelRootCss}
      className={className}
      data-testid={testId || LUNA_FILTER_PANEL_TEST_IDS.CONTAINER}
    >
      <div css={toggleWrapperCss}>
        <AIToggle
          pressed={isAISearchEnabled}
          onToggle={handleSearchWithLunaToggle}
          ariaLabel="Search with Luna"
          labelText="Search with Luna"
          qaSelector="search-with-luna-toggle"
          description="Toggle Search with Luna"
        />
      </div>

      <div css={panelCardCss(isTrafficCopFetching)}>
        {isTrafficCopFetching ? (
          <div css={loadingBoxCss} aria-live="polite">
            <AILoadingSpinner
              dataProps={{ "data-testid": "luna-loading-suggestions-spinner" }}
            />
          </div>
        ) : (
          <>
            <h1 css={screenReaderOnlyCss}>Luna search</h1>
            {renderBadges()}
            {renderContent()}
            <h2 css={screenReaderOnlyCss}>Search results:</h2>
          </>
        )}
      </div>
    </div>
  );
};

LunaFilterPanel.displayName = "LunaFilterPanel";
